import { type FC, useEffect, useRef } from 'react'
import { ImageXIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import type { Image } from '@/features/photoset/types'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

export interface ImageWithThumbnails {
	id: Image['id']
	name: Image['name']
	thumbnails: Image['thumbnails']
	ai_retouched_thumbnails?: Image['ai_retouched_thumbnails']
	retouched_thumbnails?: Image['retouched_thumbnails']
}

interface GalleryModalThumbnailsProps {
	currentImageId: Image['id']
	thumbnails:
		| 'thumbnails'
		| 'ai_retouched_thumbnails'
		| 'retouched_thumbnails'
	thumbnailsFallback?:
		| 'thumbnails'
		| 'ai_retouched_thumbnails'
		| 'retouched_thumbnails'
	images: ImageWithThumbnails[]
	onSelect: (image: ImageWithThumbnails, imageIndex: number) => void
}

export const GalleryModalThumbnails: FC<GalleryModalThumbnailsProps> = ({
	currentImageId,
	thumbnails,
	thumbnailsFallback,
	images,
	onSelect
}) => {
	const { t } = useTranslation()
	const previewScrollViewportRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (previewScrollViewportRef.current && currentImageId) {
			const selectedImageBox = document
				.getElementById(`${currentImageId}`)
				?.getBoundingClientRect()
			const scrollbarPosition =
				previewScrollViewportRef.current.scrollLeft

			if (selectedImageBox) {
				previewScrollViewportRef.current.scrollTo({
					left:
						selectedImageBox.x -
						selectedImageBox.width +
						scrollbarPosition,
					behavior: 'smooth'
				})
			}
		}
	}, [currentImageId])

	return (
		<ScrollArea
			ref={previewScrollViewportRef}
			scrollbar={<Scrollbar orientation='horizontal' />}
		>
			<div className='flex w-max gap-4 py-2 pr-6'>
				{images.map((image, index) => {
					const thumbnail =
						image[thumbnails]?.xs ??
						(thumbnailsFallback && image[thumbnailsFallback]?.xs)
					const isSelected = currentImageId === image.id
					return thumbnail ? (
						<button
							className={cn(
								'aspect-3/2 w-[140px] cursor-pointer rounded-lg bg-neutral-white',
								isSelected
									? 'shadow-[0_0_0_4px_white] outline-4 outline-neutral-500 outline-offset-4 first:ms-2 last:me-2'
									: 'border border-black/20'
							)}
							key={image.id}
							onClick={() => onSelect(image, index)}
							type='button'
						>
							{/* biome-ignore lint/correctness/useImageSize: the image is from backend and the size is not known */}
							<img
								alt={image.name}
								className={cn(
									'size-full rounded-lg object-contain'
								)}
								id={`${image.id}`}
								loading='lazy'
								src={thumbnail}
							/>
						</button>
					) : (
						<button
							className={cn(
								'flex aspect-3/2 w-[140px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg bg-neutral-white font-medium text-xs',
								isSelected
									? 'shadow-[0_0_0_4px_white] outline-4 outline-neutral-500 outline-offset-4 first:ms-2 last:me-2'
									: 'border border-black/20'
							)}
							id={`${image.id}`}
							key={image.id}
							onClick={() => onSelect(image, index)}
							type='button'
						>
							<ImageXIcon size={18} />
							<p>
								{t('errors:unavailable', {
									subject: 'preview'
								})}
							</p>
						</button>
					)
				})}
			</div>
		</ScrollArea>
	)
}

GalleryModalThumbnails.displayName = 'GalleryModalThumbnails'
